import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo, } from 'react';
import { App } from 'antd';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import { useTableView } from 'components/ResponsiveTable';
import { serializeDate } from 'components/ResponsiveTable/table-view';
import { listSpeakers } from 'api/speaker-api-v2';
import SpeakerModal from 'screens/DashboardScreen/components/SpeakerModal';
import SpeakersTable, { defaultSieve } from '../SpeakersTable';
import SpeakerDetailModal from '../SpeakersTable/SpeakerDetailModal';
import SpeakersToolbar from '../SpeakersToolbar';
const SPEAKERS_TABLE_ID = 'speakers-table'; // Must be unique across all tables in Beey!!
const SpeakersTab = ({ locales, contentHeight }) => {
    var _a;
    const [speakers, setSpeakers] = useState({
        status: 'loading',
        data: null,
    });
    const [speakerModal, setSpeakerModal] = useState(null);
    const [selectedSpeakerRows, setSelectedSpeakerRows] = useState([]);
    const { session } = useSession();
    const { message } = App.useApp();
    const tableView = useTableView(SPEAKERS_TABLE_ID, { sieve: defaultSieve });
    const { login: { user: { settings: { speakerSettings } } } } = session;
    const { sieve } = tableView.getViewParams();
    const selectedLangCodes = (_a = sieve === null || sieve === void 0 ? void 0 : sieve.langs) !== null && _a !== void 0 ? _a : speakerSettings.languageFilter;
    const selectedLangs = locales.filter((l) => selectedLangCodes.includes(l.code));
    const selectedSpeakerKeys = useMemo(() => selectedSpeakerRows.map((r) => r.key), [selectedSpeakerRows]);
    const fetchSpeakers = async () => {
        var _a;
        setSpeakers((prev) => (Object.assign(Object.assign({}, prev), { status: 'loading' })));
        const { currentPage, pageSize, sieve: currentSieve } = tableView.getViewParams();
        const { updatedDates, filters, sortBy, sortOrder, } = currentSieve !== null && currentSieve !== void 0 ? currentSieve : {};
        const result = await listSpeakers(session.connection, pageSize * (currentPage - 1), pageSize, selectedLangCodes, currentSieve === null || currentSieve === void 0 ? void 0 : currentSieve.search, serializeDate(updatedDates === null || updatedDates === void 0 ? void 0 : updatedDates[0]), serializeDate(updatedDates === null || updatedDates === void 0 ? void 0 : updatedDates[1]), ((_a = filters === null || filters === void 0 ? void 0 : filters.speakerIdActive) === null || _a === void 0 ? void 0 : _a[0]) != null ? Boolean(filters.speakerIdActive[0]) : undefined, sortBy !== undefined ? String(sortBy) : undefined, sortOrder != null ? (sortOrder === 'ascend' ? 'ascending' : 'descending') : undefined);
        if (result.isSuccess()) {
            const speakersList = result.get();
            setSpeakers({
                status: 'success',
                data: {
                    entries: speakersList.list,
                    totalCount: speakersList.totalCount,
                },
            });
        }
        else {
            setSpeakers((prev) => (Object.assign(Object.assign({}, prev), { status: 'error' })));
            void message.error(txt('failedToFetchSpeaker'));
        }
    };
    const onCloseModal = useCallback(() => {
        setSpeakerModal(null);
    }, []);
    const onEditedIdChange = useCallback((id, language) => {
        setSpeakerModal((prev) => {
            var _a;
            return ({
                mode: 'edit',
                id,
                languages: language !== null && language !== void 0 ? language : (prev !== null && prev.mode !== 'detail'
                    ? (Array.isArray(prev.languages) ? (_a = prev.languages[0]) !== null && _a !== void 0 ? _a : null : prev.languages)
                    : null),
            });
        });
    }, []);
    const onSelectChange = (_keys, newSelectedRows) => {
        setSelectedSpeakerRows(newSelectedRows);
    };
    const onDeselect = useCallback(() => {
        setSelectedSpeakerRows([]);
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(SpeakersToolbar, { tableView: tableView, localization: {
                    locales,
                    selectedLangs,
                }, onAdd: () => setSpeakerModal({ mode: 'create', languages: selectedLangs.length > 0 ? selectedLangs : locales }), onRefetch: fetchSpeakers, selectedSpeakerRows: selectedSpeakerRows, onDeselect: onDeselect }), _jsx(SpeakersTable, { speakers: speakers, tableView: tableView, onFetch: fetchSpeakers, contentHeight: contentHeight, languages: selectedLangs, onSpeakerModalChange: setSpeakerModal, onSelectChange: onSelectChange, selectedSpeakers: selectedSpeakerKeys }), speakerModal !== null && (speakerModal.mode === 'detail' ? (_jsx(SpeakerDetailModal, { uniqueId: speakerModal.id, onClose: onCloseModal })) : (_jsx(SpeakerModal, { mode: speakerModal.mode, languages: speakerModal.languages, onClose: onCloseModal, editedId: speakerModal.mode === 'edit' ? speakerModal.id : undefined, onSubmit: fetchSpeakers, onEditedIdChange: onEditedIdChange })))] }));
};
export default SpeakersTab;
